import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';
import Section from '../components/ui/Section';
import SafeIcon from '../common/SafeIcon';
import CountUp from '../components/ui/CountUp';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';

const features = [
  { icon: FaIcons.FaBowlFood, title: "Nutritious Meals", desc: "We serve over 1,000 warm, medically balanced meals every single day to ensure no child goes hungry." },
  { icon: FaIcons.FaGraduationCap, title: "Education Access", desc: "Tuition support, uniforms, and books for children who would otherwise be unable to attend school." },
  { icon: FaIcons.FaHeart, title: "Spiritual Care", desc: "A Christ-centered approach that nurtures values, hope, and emotional well-being." },
  { icon: FaIcons.FaHandsHoldingChild, title: "Advocacy & Rights", desc: "Protecting the dignity and human rights of vulnerable children in Guinea-Bissau." }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { type: "spring", stiffness: 50, damping: 20 }
  }
};

const Home = () => {
  return (
    <>
      <Helmet>
        <title>AGIR USA | Hope & Healing for Guinea-Bissau</title>
        <meta name="description" content="A Christ-centered nonprofit serving Guinea-Bissau for 25+ years. Supporting 1,000+ children daily with food and education." />
      </Helmet>

      {/* Hero Section */}
      <div className="relative h-screen min-h-[800px] flex items-center justify-center overflow-hidden">
        {/* Background Parallax */}
        <div className="absolute inset-0 z-0">
          <motion.img 
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 10, ease: "linear" }}
            src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/5d9895a8e6083669ffaa4444f6e50851.jpeg" 
            alt="Students and staff gathering in unity" 
            className="w-full h-full object-cover animate-slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/95 via-primary-dark/40 to-black/30 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-black/20"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10 text-white mt-16">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div variants={itemVariants} className="inline-flex items-center gap-3 px-5 py-2 bg-white/10 backdrop-blur-md text-secondary-light font-bold text-xs md:text-sm rounded-full mb-8 border border-white/20 shadow-lg">
              <SafeIcon icon={FaIcons.FaDove} /> 25 YEARS OF FAITH & SERVICE
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.1] mb-8 drop-shadow-2xl tracking-tight">
              Every Child <br className="hidden md:block"/>
              <span className="text-secondary italic bg-clip-text text-transparent bg-gradient-to-r from-secondary-light to-secondary">Deserves Hope.</span>
            </motion.h1>

            <motion.p variants={itemVariants} className="text-xl md:text-2xl text-gray-100 mb-12 leading-relaxed max-w-3xl mx-auto font-light drop-shadow-lg opacity-90">
              AGIR USA provides daily nutrition, education, and spiritual support to over 1,000 children in Guinea-Bissau.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button to="/get-involved" variant="secondary" size="lg" className="text-lg px-12 py-5 shadow-glow hover:scale-105 transition-transform w-full sm:w-auto">
                Donate Now
              </Button>
              <Button to="/programs" variant="outline" className="border-white text-white hover:bg-white hover:text-primary-dark shadow-lg w-full sm:w-auto" size="lg">
                See Our Impact
              </Button>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ delay: 2, duration: 2, repeat: Infinity }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/50"
        >
          <SafeIcon icon={FiIcons.FiChevronDown} className="text-3xl" />
        </motion.div>
      </div>

      {/* Trust Indicators */}
      <div className="bg-surface border-b border-gray-200/50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-8 md:gap-12 opacity-60 grayscale hover:grayscale-0 transition-all duration-700">
             <div className="flex items-center gap-2 text-primary-dark font-bold text-sm md:text-base tracking-widest uppercase">
               <SafeIcon icon={FaIcons.FaCheckCircle} /> 501(c)(3) Registered
             </div>
             <div className="flex items-center gap-2 text-primary-dark font-bold text-sm md:text-base tracking-widest uppercase">
               <SafeIcon icon={FaIcons.FaHistory} /> Est. 1998
             </div>
             <div className="flex items-center gap-2 text-primary-dark font-bold text-sm md:text-base tracking-widest uppercase">
               <SafeIcon icon={FaIcons.FaHandshake} /> Trusted Partners
             </div>
             <div className="flex items-center gap-2 text-primary-dark font-bold text-sm md:text-base tracking-widest uppercase">
               <SafeIcon icon={FaIcons.FaFileInvoiceDollar} /> Accountable
             </div>
          </div>
        </div>
      </div>

      {/* Mission Snapshot - Staggered Reveal */}
      <Section bg="white" className="overflow-hidden">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:w-1/2"
          >
            <div className="inline-block px-3 py-1 bg-primary/10 text-primary font-bold text-xs tracking-widest uppercase rounded-full mb-6">Our Mission</div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-dark mb-8 leading-tight">
              Compassion in Action,<br/> 
              <span className="text-accent underline decoration-secondary decoration-4 underline-offset-4">Stability</span> for the Future.
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              In Guinea-Bissau, poverty and food insecurity often steal the future from the most vulnerable. Since 1998, AGIR USA has stood in the gap, providing a foundation of health and education for the next generation.
            </p>
            <div className="grid grid-cols-2 gap-8 mb-10">
              <div className="flex gap-4 group">
                <div className="text-secondary text-2xl group-hover:scale-110 transition-transform"><SafeIcon icon={FaIcons.FaCross} /></div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Christ-Centered</h4>
                  <p className="text-sm text-gray-500">Guided by faith, love, and service.</p>
                </div>
              </div>
              <div className="flex gap-4 group">
                <div className="text-secondary text-2xl group-hover:scale-110 transition-transform"><SafeIcon icon={FaIcons.FaGlobeAfrica} /></div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Locally Led</h4>
                  <p className="text-sm text-gray-500">Empowering Guinean leadership.</p>
                </div>
              </div>
            </div>
            <Button to="/about" variant="primary">Read Our Full Story</Button>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:w-1/2 relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/5] md:aspect-square lg:aspect-[4/5]">
              <img src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/b92ed635c12fcaf48491707e12eab5cd.jpeg" alt="Student smiling at desk" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60"></div>
            </div>
            
            {/* Floating Stats Card */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="absolute -bottom-8 -left-8 md:bottom-10 md:-left-10 bg-surface p-8 rounded-xl shadow-card border border-white/50 max-w-xs hidden md:block"
            >
              <div className="flex items-center gap-4 mb-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Impact Live</span>
              </div>
              <div className="text-4xl font-heading font-bold text-primary-dark mb-1">
                <CountUp end={25} suffix="+" />
              </div>
              <div className="text-sm text-gray-600 font-medium">Years of consistent service and community transformation.</div>
            </motion.div>
          </motion.div>
        </div>
      </Section>

      {/* What We Do - Interactive Grid */}
      <Section bg="surface">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary-dark mb-6">Our Daily Commitment</h2>
          <p className="text-gray-600 text-lg leading-relaxed">We address the immediate needs of children while building long-term pathways to stability through four key pillars.</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1, duration: 0.5 }}
              whileHover={{ y: -10 }}
              className="bg-white p-8 rounded-2xl shadow-soft hover:shadow-card transition-all duration-300 group border border-gray-100/50"
            >
              <div className="w-16 h-16 bg-primary/5 text-primary rounded-2xl flex items-center justify-center mb-8 text-3xl group-hover:bg-primary group-hover:text-white transition-all duration-300 shadow-sm">
                <SafeIcon icon={feature.icon} />
              </div>
              <h3 className="font-heading font-bold text-xl mb-4 text-primary-dark">{feature.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">{feature.desc}</p>
              <Link to="/programs" className="text-primary font-bold text-sm flex items-center gap-2 group/link hover:text-secondary transition-colors">
                Learn More <SafeIcon icon={FiIcons.FiArrowRight} className="group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Stats Parallax Section */}
      <div className="relative py-32 md:py-48 bg-primary-dark overflow-hidden attachment-fixed">
        <div className="absolute inset-0 z-0">
          <img src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/7ab728fa003eb0944fc5dd69f70d57d4.jpeg" className="w-full h-full object-cover opacity-10 mix-blend-overlay" alt="Classroom texture" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark via-transparent to-primary-dark"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 text-center divide-x divide-white/10">
            {[
              { n: 1000, label: "Children Daily", suffix: "+" },
              { n: 25, label: "Years Active", suffix: "+" },
              { n: 3, label: "Meals Served", suffix: "M+" }, // Custom handling for Million
              { n: 100, label: "Transparency", suffix: "%" }
            ].map((stat, i) => (
              <div key={i} className="px-4">
                <div className="text-4xl md:text-6xl font-heading font-bold text-secondary mb-3">
                  {stat.suffix === "M+" ? <CountUp end={3} suffix="M+" /> : <CountUp end={stat.n} suffix={stat.suffix} />}
                </div>
                <div className="text-xs md:text-sm uppercase tracking-widest text-gray-300 font-bold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA - Emotional */}
      <Section bg="surface" className="relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-secondary to-transparent"></div>
        <div className="bg-white rounded-3xl p-10 md:p-20 text-center shadow-card border border-gray-100 max-w-5xl mx-auto relative overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/5 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/5 rounded-full translate-y-1/2 -translate-x-1/3 blur-3xl"></div>
          
          <div className="relative z-10">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary-dark mb-6">Partner With Us Today</h2>
            <p className="text-gray-600 text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
              Your gift is more than a donation—it's a warm meal, a school book, and a message of love to a child waiting for hope.
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Button to="/get-involved" variant="primary" size="lg" className="px-12 py-4 text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1">
                Donate via PayPal
              </Button>
              <Button to="/contact" variant="outline" size="lg" className="px-12 py-4 text-lg">
                Contact Us
              </Button>
            </div>
            <p className="mt-8 text-xs text-gray-400 font-medium uppercase tracking-widest">Secure • Tax-Deductible • Impactful</p>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Home;